// Scan assembly — spec §3.3. A label may split its data over several codes (a GS1-128
// with the GTIN and lot, another with the length and best-before). Codes read within
// a short window whose AIs do not conflict are merged into one result; a code lacking
// what the identity scheme needs is held until a complementary code completes it
// ("assemble-required" partial-code policy).

import { parseScan, toAiDataStr, type GS1Elements } from "@trail/core";

export interface Assembler {
  /** Returns the merged elements when `elements` completes an earlier code of the same label. */
  tryAssemble(elements: GS1Elements): Promise<GS1Elements | null>;
  /** Hold an incomplete code; `takeHeld` returns the raw data of codes held within the window. */
  hold(data: string, elements: GS1Elements): void;
  takeHeld(): string[];
  heldCount(): number;
  clear(): void;
}

export function createAssembler(windowMs = 2500, now: () => number = () => performance.now()): Assembler {
  let recent: Array<{ at: number; elements: GS1Elements }> = [];
  let partial: Array<{ data: string; at: number }> = [];
  return {
    async tryAssemble(elements) {
      // A logistic unit's code (AI 00) is complete on its own and belongs to no trade
      // item's label: it never takes part in assembly, in either direction.
      if (elements.sscc !== null) return null;
      const t = now();
      recent = recent.filter((r) => t - r.at < windowMs);
      const complement = recent.find((r) => {
        const a = r.elements.ai;
        const b = elements.ai;
        const overlap = Object.keys(a).filter((k) => k in b);
        if (overlap.some((k) => a[k] !== b[k])) return false; // conflicting data: another label
        return Object.keys(b).some((k) => !(k in a)) && Object.keys(a).some((k) => !(k in b)); // each side adds something
      });
      recent.push({ at: t, elements });
      if (!complement) return null;
      try {
        return await parseScan(toAiDataStr({ ...complement.elements.ai, ...elements.ai }));
      } catch {
        return null;
      }
    },
    hold(data, _elements) {
      partial.push({ data, at: now() });
    },
    takeHeld() {
      const t = now();
      const held = partial.filter((p) => t - p.at < windowMs).map((p) => p.data);
      partial = [];
      return held;
    },
    heldCount() {
      const t = now();
      partial = partial.filter((p) => t - p.at < windowMs);
      return partial.length;
    },
    clear() {
      recent = [];
      partial = [];
    },
  };
}
